// src/services/dashboardService.js

import axios from "axios";
import { API_URL } from "../../api_calls";
import keycloak from "../../keycloak";

async function getAuthHeaders() {
  if (!keycloak.authenticated) {
    throw new Error("User not authenticated");
  }

  try {
    await keycloak.updateToken(30);
  } catch (err) {
    console.error("Token refresh failed", err);
  }

  return {
    Authorization: `Bearer ${keycloak.token}`,
  };
}

function buildParams(filters) {
  const params = {};

  if (filters?.dateFrom) params.date_from = filters.dateFrom;
  if (filters?.dateTo) params.date_to = filters.dateTo;

  if (filters?.centerCodes?.length) {
    params.center_codes = filters.centerCodes;
  }

  return params;
}

const AXIOS_CFG = {
  withCredentials: true,
  paramsSerializer: (params) => {
    const sp = new URLSearchParams();

    for (const [k, v] of Object.entries(params || {})) {
      if (v == null) continue;

      if (Array.isArray(v)) {
        for (const item of v) sp.append(k, String(item));
      } else {
        sp.append(k, String(v));
      }
    }

    return sp.toString();
  },
};

async function authAxiosGet(url, params = {}) {
  const headers = await getAuthHeaders();

  const res = await axios.get(url, {
    ...AXIOS_CFG,
    params,
    headers,
  });

  return res.data;
}

// --------------------
// DASHBOARD ENDPOINTS
// --------------------

export async function fetchCenters() {
  return authAxiosGet(`${API_URL}/dashboard/centers`);
}

export async function fetchMiniByCenter(filters) {
  const params = buildParams(filters);
  return authAxiosGet(`${API_URL}/dashboard/mini-by-center`, params);
}

export async function fetchKpis(filters) {
  const params = buildParams(filters);
  return authAxiosGet(`${API_URL}/dashboard/kpis`, params);
}

export async function fetchTimeseries(metric, filters) {
  const params = { metric, interval: "day", ...buildParams(filters) };
  return authAxiosGet(`${API_URL}/dashboard/timeseries`, params);
}

export async function fetchCentersTimeseries(filters, top = 5) {
  const params = { top, interval: "day", ...buildParams(filters) };
  return authAxiosGet(`${API_URL}/dashboard/centers/timeseries`, params);
}

export async function fetchYesNoSummary(filters) {
  const params = buildParams(filters);
  return authAxiosGet(`${API_URL}/dashboard/yesno-summary`, params);
}

export async function fetchTopCategories(field, filters, limit = 5) {
  const params = { field, limit, ...buildParams(filters) };
  return authAxiosGet(`${API_URL}/dashboard/top-categories`, params);
}

export async function fetchBloc1Stats(filters) {
  const params = buildParams(filters);
  return authAxiosGet(`${API_URL}/dashboard/bloc1-stats`, params);
}

export async function fetchBloc0Stats(filters) {
  const params = buildParams(filters);
  return authAxiosGet(`${API_URL}/dashboard/bloc0-stats`, params);
}

export async function fetchBloc2Stats(filters) {
  const params = buildParams(filters);
  return authAxiosGet(`${API_URL}/dashboard/bloc2-stats`, params);
}

export async function fetchBloc3Stats(filters) {
  const params = buildParams(filters);
  return authAxiosGet(`${API_URL}/dashboard/bloc3-stats`, params);
}

export async function fetchBloc4Stats(filters) {
  const params = buildParams(filters);
  return authAxiosGet(`${API_URL}/dashboard/bloc4-stats`, params);
}

export async function fetchBloc5Stats(filters) {
  const params = buildParams(filters);
  return authAxiosGet(`${API_URL}/dashboard/bloc5-stats`, params);
}

export async function fetchBloc6Stats(filters) {
  const params = buildParams(filters);
  return authAxiosGet(`${API_URL}/dashboard/bloc6-stats`, params);
}

export async function fetchBloc7Stats(filters) {
  const params = buildParams(filters);
  return authAxiosGet(`${API_URL}/dashboard/bloc7-stats`, params);
}

// --------------------
// CENTERS CATALOG
// --------------------

export async function fetchAllCenters() {
  const headers = await getAuthHeaders();

  const res = await axios.get(`${API_URL}/centers`, {
    headers,
    withCredentials: true,
  });

  return res.data;
}
